// Monthly Recap — the "how did the month go?" page.
//
// One request to GET /statistics/recap fills every panel. The date filter is
// the only input: a preset picker for the common windows plus from/to inputs
// for anything else. Windows are Sydney-local on the server, so what the admin
// picks is what the Australian calendar shows.

import { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

// @mui
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Icon from '@mui/material/Icon'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'

// MD
import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDTypography from 'components/MDTypography'

// Layout
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'examples/Navbars/DashboardNavbar'
import Footer from 'examples/Footer'
import Loader from 'examples/Loader'
import ComplexStatisticsCard from 'examples/Cards/StatisticsCards/ComplexStatisticsCard'

// Services
import { jsPDF } from 'jspdf'

// Services
import { useAppServices } from 'hook/services'

// --- helpers ---------------------------------------------------------------

const money = (n) =>
  `$${Number(n || 0).toLocaleString('en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

const num = (n) => Number(n || 0).toLocaleString('en-AU')

// "yyyy-mm-dd" for a Date read in Sydney — keeps the inputs agreeing with the
// server's window even when the admin's machine is in another timezone.
const sydneyIso = (d) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Australia/Sydney',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d)
  const get = (t) => parts.find((p) => p.type === t).value
  return `${get('year')}-${get('month')}-${get('day')}`
}

// Last 12 Sydney months, newest first.
const monthOptions = (() => {
  const todayIso = sydneyIso(new Date())
  const [y, m] = todayIso.split('-').map((n) => parseInt(n, 10))
  const out = []
  for (let i = 0; i < 12; i++) {
    const d = new Date(Date.UTC(y, m - 1 - i, 1))
    out.push({
      value: `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleString('en-AU', { month: 'long', year: 'numeric', timeZone: 'UTC' }),
    })
  }
  return out
})()

const currentMonth = monthOptions[0].value
const previousMonth = monthOptions[1].value

// Shifts a Sydney "yyyy-mm-dd" by N days without dragging in a date library.
const shiftDays = (iso, days) => {
  const [y, m, d] = iso.split('-').map((n) => parseInt(n, 10))
  const shifted = new Date(Date.UTC(y, m - 1, d + days))
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}-${String(
    shifted.getUTCDate()
  ).padStart(2, '0')}`
}

// A stat tile that also carries a caption, which ComplexStatisticsCard doesn't.
// --- end-of-month report ----------------------------------------------------
//
// Drawn straight into a PDF with jsPDF's text API rather than screenshotting a
// page, so the text stays selectable, the file stays small, and the download
// starts on the click instead of routing through the print dialog.

const PDF = {
  margin: 16,
  width: 210, // A4 in mm
  height: 297,
  ink: [31, 41, 55],
  muted: [107, 114, 128],
  brand: [13, 71, 161],
  accent: [26, 115, 232],
  rule: [229, 231, 235],
  faint: [243, 246, 250],
}

/** Builds the file, then hands it to the browser as a download. */
const downloadRecapPdf = (data) => {
  const b = data.bookings || {}
  const users = data.users || {}
  const listings = data.listings || {}
  const boost = data.boost || {}
  const dir = data.creativeDirectory || {}
  const range = data.range || {}

  // A custom from/to window is not a month, so the heading does not claim to be
  // an end-of-month recap.
  const isMonth = range.preset === 'month'
  const title = isMonth ? 'Aloca — End of month recap' : 'Aloca — Recap'
  const period = isMonth
    ? `${range.label || ''}  ·  ${range.from || ''} to ${range.to || ''}`
    : `${range.from || ''} to ${range.to || ''}`

  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const right = PDF.width - PDF.margin
  let y = PDF.margin

  const setInk = (rgb) => doc.setTextColor(rgb[0], rgb[1], rgb[2])

  // Starts a new page when the next block would run off the bottom.
  const room = (needed) => {
    if (y + needed <= PDF.height - PDF.margin - 10) return
    doc.addPage()
    y = PDF.margin
  }

  // ── Header
  doc.setFont('helvetica', 'bold').setFontSize(17)
  setInk(PDF.brand)
  doc.text(title, PDF.margin, y + 5)
  y += 11

  doc.setFont('helvetica', 'normal').setFontSize(9.5)
  setInk(PDF.ink)
  doc.text(period, PDF.margin, y)
  y += 5

  doc.setFontSize(8)
  setInk(PDF.muted)
  doc.text(
    `All dates and totals calculated in ${range.timezone || 'Australia/Sydney'}.`,
    PDF.margin,
    y
  )
  y += 4

  doc.setDrawColor(PDF.accent[0], PDF.accent[1], PDF.accent[2]).setLineWidth(0.8)
  doc.line(PDF.margin, y, right, y)
  y += 8

  // ── Headline figures, side by side
  const kpis = [
    { k: 'TOTAL BOOKINGS', v: num(b.total) },
    { k: 'AVERAGE ORDER VALUE', v: money(b.aov) },
  ]
  const boxW = (right - PDF.margin - 4) / kpis.length
  kpis.forEach((kpi, i) => {
    const x = PDF.margin + i * (boxW + 4)
    doc.setFillColor(PDF.faint[0], PDF.faint[1], PDF.faint[2])
    doc.setDrawColor(PDF.rule[0], PDF.rule[1], PDF.rule[2]).setLineWidth(0.2)
    doc.roundedRect(x, y, boxW, 17, 1.5, 1.5, 'FD')

    doc.setFont('helvetica', 'normal').setFontSize(7)
    setInk(PDF.muted)
    doc.text(kpi.k, x + 4, y + 6)

    doc.setFont('helvetica', 'bold').setFontSize(13)
    setInk(PDF.brand)
    doc.text(String(kpi.v), x + 4, y + 13)
  })
  y += 25

  // ── One section: heading, optional blurb, then label/value rows.
  const section = (heading, blurb, rows) => {
    const live = rows.filter(Boolean)
    if (!live.length) return

    room(18 + live.length * 7)

    doc.setFillColor(PDF.accent[0], PDF.accent[1], PDF.accent[2])
    doc.rect(PDF.margin, y - 3.4, 1.2, 4.6, 'F')

    doc.setFont('helvetica', 'bold').setFontSize(11)
    setInk(PDF.brand)
    doc.text(heading, PDF.margin + 3.5, y)
    y += blurb ? 5 : 6

    if (blurb) {
      doc.setFont('helvetica', 'normal').setFontSize(7.5)
      setInk(PDF.muted)
      doc.text(blurb, PDF.margin + 3.5, y)
      y += 5
    }

    for (const row of live) {
      room(9)

      doc.setFont('helvetica', 'normal').setFontSize(9)
      setInk(PDF.ink)
      doc.text(String(row.label), PDF.margin, y)

      doc.setFont('helvetica', 'bold')
      doc.text(String(row.value), right, y, { align: 'right' })

      if (row.note) {
        y += 3.6
        doc.setFont('helvetica', 'normal').setFontSize(7)
        setInk(PDF.muted)
        doc.text(String(row.note), PDF.margin, y)
      }

      y += 3
      doc.setDrawColor(238, 242, 247).setLineWidth(0.15)
      doc.line(PDF.margin, y, right, y)
      y += 4.5
    }

    // The divider that separates one section from the next.
    y += 1
    doc.setDrawColor(PDF.rule[0], PDF.rule[1], PDF.rule[2]).setLineWidth(0.4)
    doc.line(PDF.margin, y, right, y)
    y += 8
  }

  const row = (label, value, note) => ({ label, value, note })

  section('Bookings', 'Every booking made in this window, and what it was worth.', [
    row('Bookings made', num(b.total)),
    row('Still standing', num(b.live), 'Not cancelled or declined'),
    row('Cancelled', `${num(b.cancellations?.count)} · ${money(b.cancellations?.value)}`),
    row('Declined', `${num(b.declines?.count)} · ${money(b.declines?.value)}`),
    row('Average order value', money(b.aov), 'Excludes cancelled and declined bookings'),
    row('Boost revenue', money(boost.revenue), `${num(boost.weeksSold)} listing-week(s) sold`),
    row('Directory revenue', money(dir.revenue), `${num(dir.payments)} payment(s) captured`),
  ])

  const bookingStates = b.byState || []
  if (bookingStates.length) {
    section(
      'Bookings by state',
      'Where the demand is. Cancelled and declined bookings are included.',
      bookingStates.map((st) =>
        row(
          st.state,
          num(st.count),
          ''
        )
      )
    )
  }

  section('Hosts', 'People who list a space.', [
    row('Hosts in total', num(users.hosts?.total)),
    row('Joined this period', num(users.hosts?.new)),
  ])

  section('Creatives', 'People who book a space.', [
    row('Creatives in total', num(users.creatives?.total)),
    row('Joined this period', num(users.creatives?.new)),
  ])

  const foundUsRows = data.foundUs?.byChannel || []
  if (foundUsRows.length) {
    section(
      'How new users found us',
      'Self-reported at signup, for everyone who joined in this window.',
      foundUsRows.map((r) => row(r.channel, num(r.count)))
    )
  }

  section('Listings', 'What hosts put up, and where each listing sits today.', [
    row('New listings', num(listings.new)),
    row('…of which published', num(listings.newPublished)),
    row('Published', num(listings.totalPublished), 'Live on the site right now'),
    row('Paused', num(listings.totalPaused), 'Taken off the site by the host'),
    row('Draft', num(listings.totalDraft), 'Started but never published'),
    row('All listings', num(listings.total), 'Everything except deleted'),
  ])

  const states = listings.byState || []
  if (states.length) {
    section(
      'Published listings by state',
      'Where the live listings actually are.',
      states.map((st) =>
        row(
          st.state,
          num(st.count),
          listings.totalPublished
            ? `${Math.round((st.count / listings.totalPublished) * 100)}% of published`
            : ''
        )
      )
    )
  }

  section('Creative directory', 'Paid profiles that make creatives discoverable to brands.', [
    row('New submissions', num(dir.submissions)),
    row('Approved', num(dir.approved)),
    row('Approved and live in total', num(dir.liveTotal)),
  ])

  const brands = (data.topBrands || []).slice(0, 10)
  if (brands.length) {
    section(
      'Top spending brands',
      'Ranked by what they spent on bookings in this window.',
      brands.map((brand, i) =>
        row(
          `${i + 1}. ${brand.company || brand.name || 'Unnamed'}`,
          money(brand.spend),
          `${num(brand.bookings)} booking(s)`
        )
      )
    )
  }

  // ── Footer on every page
  const generated = new Date().toLocaleString('en-AU', { timeZone: 'Australia/Sydney' })
  const pages = doc.getNumberOfPages()
  for (let page = 1; page <= pages; page += 1) {
    doc.setPage(page)
    doc.setFont('helvetica', 'normal').setFontSize(7)
    setInk(PDF.muted)
    doc.text(
      `Generated ${generated} (Australia/Sydney) · Aloca & Co Pty Ltd`,
      PDF.margin,
      PDF.height - 10
    )
    doc.text(`${page} / ${pages}`, right, PDF.height - 10, { align: 'right' })
  }

  const stamp = isMonth ? (range.label || '').replace(/\s+/g, '-') : `${range.from}_${range.to}`
  doc.save(`Aloca-recap-${stamp || 'report'}.pdf`)
}

function StatCard({ icon, color, title, value, caption }) {
  return (
    <ComplexStatisticsCard
      color={color}
      icon={icon}
      title={title}
      count={value}
      percentage={{ color: 'text', amount: '', label: caption || '' }}
    />
  )
}

StatCard.defaultProps = { color: 'info', caption: '' }
StatCard.propTypes = {
  icon: PropTypes.string.isRequired,
  color: PropTypes.string,
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  caption: PropTypes.string,
}

// A compact labelled row used inside the Boost / Directory panels.
function StatLine({ label, value, hint }) {
  return (
    <MDBox display="flex" justifyContent="space-between" alignItems="baseline" py={0.75}>
      <MDBox display="flex" alignItems="center" gap={0.5}>
        <MDTypography variant="button" color="text">
          {label}
        </MDTypography>
        {hint ? (
          <Tooltip title={hint}>
            <Icon fontSize="small" sx={{ color: '#9ca3af', fontSize: 14 }}>
              info
            </Icon>
          </Tooltip>
        ) : null}
      </MDBox>
      <MDTypography variant="button" fontWeight="medium">
        {value}
      </MDTypography>
    </MDBox>
  )
}

StatLine.defaultProps = { hint: '' }
StatLine.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.node.isRequired,
  hint: PropTypes.string,
}

function PanelCard({ title, icon, color, children }) {
  return (
    <Card sx={{ height: '100%' }}>
      <MDBox
        mx={2}
        mt={-3}
        py={2}
        px={2}
        variant="gradient"
        bgColor={color}
        borderRadius="lg"
        coloredShadow={color}
        display="flex"
        alignItems="center"
        gap={1}
      >
        <Icon fontSize="small" sx={{ color: 'white' }}>
          {icon}
        </Icon>
        <MDTypography variant="h6" color="white">
          {title}
        </MDTypography>
      </MDBox>
      <MDBox p={3} pt={2}>
        {children}
      </MDBox>
    </Card>
  )
}

PanelCard.defaultProps = { color: 'info' }
PanelCard.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  color: PropTypes.string,
  children: PropTypes.node.isRequired,
}

// A row of stat tiles. The tiles are described as data rather than repeated as
// JSX, so the same tile can appear on Overview and on its own tab without the
// markup existing twice.
function TileRow({ tiles }) {
  return (
    <MDBox display="flex" flexWrap="wrap" gap={3}>
      {tiles.map((tile) => (
        <MDBox key={tile.title} mt={3} mb={1.5} sx={{ flex: '1 1 230px', minWidth: 230 }}>
          <StatCard
            color={tile.color}
            icon={tile.icon}
            title={tile.title}
            value={tile.value}
            caption={tile.caption}
          />
        </MDBox>
      ))}
    </MDBox>
  )
}

TileRow.propTypes = { tiles: PropTypes.array.isRequired }

// Small heading used to label a group inside a tab.
function SectionHeading({ title, hint }) {
  return (
    <MDBox mt={1} mb={0.5}>
      <MDTypography variant="h6" fontWeight="medium">
        {title}
      </MDTypography>
      {hint ? (
        <MDTypography variant="button" color="text">
          {hint}
        </MDTypography>
      ) : null}
    </MDBox>
  )
}

SectionHeading.defaultProps = { hint: '' }
SectionHeading.propTypes = { title: PropTypes.string.isRequired, hint: PropTypes.string }

// --- page ------------------------------------------------------------------

function MonthlyRecap() {
  const AppService = useAppServices()

  const [preset, setPreset] = useState('this-month')
  const [month, setMonth] = useState(currentMonth)
  const [from, setFrom] = useState(sydneyIso(new Date()))
  const [to, setTo] = useState(sydneyIso(new Date()))
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Turns the current filter state into the query the API expects. Presets that
  // map onto a calendar month send `month=`; everything else sends from/to.
  const buildQuery = useCallback(() => {
    const params = new URLSearchParams()
    const today = sydneyIso(new Date())

    if (preset === 'this-month') params.set('month', currentMonth)
    else if (preset === 'last-month') params.set('month', previousMonth)
    else if (preset === 'pick-month') params.set('month', month)
    else if (preset === 'last-30') {
      params.set('from', shiftDays(today, -29))
      params.set('to', today)
    } else if (preset === 'last-90') {
      params.set('from', shiftDays(today, -89))
      params.set('to', today)
    } else {
      params.set('from', from)
      params.set('to', to)
    }
    return params.toString()
  }, [preset, month, from, to])

  const fetchRecap = useCallback(
    async (query) => {
      setLoading(true)
      setError(null)
      try {
        const { response } = await AppService.statistics.recap({ query, toaster: false })
        if (response?.success) setData(response.data)
        else {
          setData(null)
          setError(response?.message || 'Could not load the recap.')
        }
      } catch (err) {
        console.error('❌ Error fetching recap:', err)
        setData(null)
        setError(err?.message || 'Could not load the recap.')
      } finally {
        setLoading(false)
      }
    },
    [AppService]
  )

  // Presets refetch immediately; a custom range waits for Apply so the admin
  // isn't firing a request between picking `from` and picking `to`.
  useEffect(() => {
    if (preset === 'custom') return
    fetchRecap(buildQuery())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, month])

  // Builds the PDF from exactly what is on screen and downloads it. Nothing is
  // fetched again, so the file can never disagree with the numbers just read.
  const openReport = useCallback(() => {
    if (!data) return

    try {
      downloadRecapPdf(data)
    } catch (err) {
      console.error('Report error:', err)
      setError('The report could not be generated. Please try again.')
    }
  }, [data])


  // Which section the admin is looking at. The filter bar stays put above it,
  // because the window applies to every section.
  const [tab, setTab] = useState(0)

  // Every tile described once, grouped the way an admin thinks about them.
  // Overview borrows the four that answer "how did the month go?".
  const tiles = useMemo(() => {
    if (!data) return { trade: [], people: [], listings: [], boost: [], directory: [] }

    const b = data.bookings || {}
    const listings = data.listings || {}
    const users = data.users || {}
    const bst = data.boost || {}
    const dir = data.creativeDirectory || {}

    return {
      trade: [
        {
          color: 'primary',
          icon: 'event',
          title: 'Total bookings',
          value: num(b.total),
          caption: `${num(b.live)} still standing`,
        },
        {
          color: 'error',
          icon: 'cancel',
          title: 'Cancellations',
          value: num(b.cancellations?.count),
          caption: `${money(b.cancellations?.value)} of bookings`,
        },
        {
          color: 'warning',
          icon: 'do_not_disturb_on',
          title: 'Declines',
          value: num(b.declines?.count),
          caption: `${money(b.declines?.value)} of bookings`,
        },
        {
          color: 'success',
          icon: 'payments',
          title: 'AOV',
          value: money(b.aov),
          caption: 'excl. cancelled / declined',
        },
      ],
      people: [
        {
          color: 'dark',
          icon: 'villa',
          title: 'Hosts (total)',
          value: num(users.hosts?.total),
          caption: 'Everyone with a host account',
        },
        {
          color: 'dark',
          icon: 'person_add',
          title: 'New hosts',
          value: num(users.hosts?.new),
          caption: 'Joined in this window',
        },
        {
          color: 'primary',
          icon: 'photo_camera',
          title: 'Creatives (total)',
          value: num(users.creatives?.total),
          caption: 'Everyone with a creative account',
        },
        {
          color: 'primary',
          icon: 'person_add',
          title: 'New creatives',
          value: num(users.creatives?.new),
          caption: 'Joined in this window',
        },
      ],
      listings: [
        {
          color: 'info',
          icon: 'add_home',
          title: 'New listings',
          value: num(listings.new),
          caption: `${num(listings.newPublished)} of them published`,
        },
        {
          color: 'success',
          icon: 'check_circle',
          title: 'Published',
          value: num(listings.totalPublished),
          caption: 'Live on the site right now',
        },
        {
          color: 'warning',
          icon: 'pause_circle',
          title: 'Paused',
          value: num(listings.totalPaused),
          caption: 'Taken off the site by the host',
        },
        {
          color: 'secondary',
          icon: 'edit_note',
          title: 'Draft',
          value: num(listings.totalDraft),
          caption: 'Started but never published',
        },
        {
          color: 'dark',
          icon: 'inventory_2',
          title: 'All listings',
          value: num(listings.total),
          caption: 'Published, paused and draft together',
        },
      ],
      boost: [
        {
          color: 'success',
          icon: 'shopping_cart',
          title: 'Hosts who bought',
          value: num(bst.hostsBoosting),
          caption: `${num(bst.checkouts)} checkout(s) in this window`,
        },
        {
          color: 'success',
          icon: 'payments',
          title: 'Boost revenue',
          value: money(bst.revenue),
          caption: `${num(bst.weeksSold)} listing-week(s) sold`,
        },
        {
          color: 'info',
          icon: 'campaign',
          title: 'Listings boosted',
          value: num(bst.listingsBoosted),
          caption: 'Distinct listings bought for',
        },
        {
          color: 'dark',
          icon: 'bolt',
          title: 'Live right now',
          value: num(bst.activeNow),
          caption: 'Boosts running as of today',
        },
      ],
      directory: [
        {
          color: 'warning',
          icon: 'shopping_cart',
          title: 'Creatives who paid',
          value: num(dir.payments),
          caption: 'Payments captured in this window',
        },
        {
          color: 'warning',
          icon: 'payments',
          title: 'Subscription revenue',
          value: money(dir.revenue),
          caption: 'Collected in this window',
        },
        {
          color: 'info',
          icon: 'how_to_reg',
          title: 'New submissions',
          value: num(dir.submissions),
          caption: `${num(dir.approved)} approved in this window`,
        },
        {
          color: 'dark',
          icon: 'verified',
          title: 'Approved & live',
          value: num(dir.liveTotal),
          caption: 'Profiles visible to brands today',
        },
      ],
    }
  }, [data])

  // Rows for the listings table. The dot colour matches the tile above it, so a
  // status reads the same in both places.
  const listingBreakdown = useMemo(() => {
    const l = data?.listings
    if (!l) return []

    return [
      {
        label: 'Published',
        count: l.totalPublished,
        dot: '#4CAF50',
      },
      {
        label: 'Paused',
        count: l.totalPaused,
        dot: '#fb8c00',
      },
      {
        label: 'Draft',
        count: l.totalDraft,
        dot: '#747b8a',
      },
      ...(l.totalOther
        ? [
            {
              label: 'Other',
              count: l.totalOther,
              dot: '#c7ccd1',
            },
          ]
        : []),
    ]
  }, [data])

  const TABS = [
    { label: 'Bookings', icon: 'event_note' },
    { label: 'Users', icon: 'diversity_3' },
    { label: 'Listings', icon: 'home_work' },
    { label: 'Boost', icon: 'rocket_launch' },
    { label: 'Creative directory', icon: 'groups' },
  ]

  const bookings = data?.bookings
  const directory = data?.creativeDirectory
  const boost = data?.boost

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* ---------- Filter bar ---------- */}
        <Card sx={{ mb: 3 }}>
          <MDBox
            mx={2}
            mt={-3}
            py={2}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            <MDTypography variant="h6" color="white">
              Recap the Month
            </MDTypography>
            {data?.range ? (
              <Chip
                label={`${data.range.label}  ·  ${data.range.from} → ${data.range.to} (${data.range.timezone})`}
                sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
            ) : null}
          </MDBox>

          <MDBox p={3}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Period"
                  value={preset}
                  onChange={(e) => setPreset(e.target.value)}
                >
                  <MenuItem value="this-month">This month</MenuItem>
                  <MenuItem value="last-month">Last month</MenuItem>
                  <MenuItem value="pick-month">Pick a month</MenuItem>
                  <MenuItem value="last-30">Last 30 days</MenuItem>
                  <MenuItem value="last-90">Last 90 days</MenuItem>
                  <MenuItem value="custom">Custom range</MenuItem>
                </TextField>
              </Grid>

              {preset === 'pick-month' ? (
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Month"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                  >
                    {monthOptions.map((m) => (
                      <MenuItem key={m.value} value={m.value}>
                        {m.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              ) : null}

              {preset === 'custom' ? (
                <>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      type="date"
                      fullWidth
                      size="small"
                      label="From"
                      value={from}
                      onChange={(e) => setFrom(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      type="date"
                      fullWidth
                      size="small"
                      label="To"
                      value={to}
                      onChange={(e) => setTo(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md="auto">
                    <MDButton
                      variant="gradient"
                      color="info"
                      disabled={!from || !to || from > to}
                      onClick={() => fetchRecap(buildQuery())}
                    >
                      Apply
                    </MDButton>
                  </Grid>
                </>
              ) : null}

              <Grid item xs={12} md="auto">
                <MDButton
                  variant="outlined"
                  color="info"
                  onClick={() => fetchRecap(buildQuery())}
                  disabled={loading || (preset === 'custom' && (!from || !to || from > to))}
                >
                  <Icon sx={{ mr: 0.5 }}>refresh</Icon> Refresh
                </MDButton>
              </Grid>

              <Grid item xs={12} md="auto">
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={openReport}
                  disabled={loading || !data}
                >
                  <Icon sx={{ mr: 0.5 }}>description</Icon> Generate report
                </MDButton>
              </Grid>
            </Grid>

            {preset === 'custom' && from && to && from > to ? (
              <MDTypography variant="caption" color="error" mt={1} display="block">
                &ldquo;From&rdquo; must be on or before &ldquo;To&rdquo;.
              </MDTypography>
            ) : null}
          </MDBox>
        </Card>

        {loading ? (
          <Loader />
        ) : error ? (
          <Card>
            <MDBox p={4} textAlign="center">
              <MDTypography variant="button" color="error">
                {error}
              </MDTypography>
            </MDBox>
          </Card>
        ) : !data ? null : (
          <>            {/* ---------- Sections ---------- */}
            <Card sx={{ mb: 2 }}>
              <Tabs
                value={tab}
                onChange={(event, next) => setTab(next)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  px: 1,
                  '& .MuiTab-root': { minHeight: 56, fontWeight: 600 },
                }}
              >
                {TABS.map((section) => (
                  <Tab
                    key={section.label}
                    label={section.label}
                    icon={<Icon fontSize="small">{section.icon}</Icon>}
                    iconPosition="start"
                  />
                ))}
              </Tabs>
            </Card>

            {tab === 0 ? (
              <>
                <SectionHeading
                  title="Bookings"
                  hint="What came in, what fell over, and what it was worth."
                />
                <TileRow tiles={tiles.trade} />


            {/* ---------- Top spending brands ---------- */}
            <Grid container spacing={3} mt={0.5}>
              <Grid item xs={12}>
                <MDBox mt={3} mb={3}>
                  <PanelCard title="Top spending brands" icon="workspace_premium" color="primary">
                    {data.topBrands.length === 0 ? (
                      <MDTypography variant="button" color="text">
                        No spend recorded in this period.
                      </MDTypography>
                    ) : (
                      <MDBox sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                          <TableHead sx={{ display: 'table-header-group' }}>
                            <TableRow>
                              <TableCell>#</TableCell>
                              <TableCell>Brand / Creative</TableCell>
                              <TableCell>Email</TableCell>
                              <TableCell align="right">Bookings</TableCell>
                              <TableCell align="right">Total spend</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {data.topBrands.map((b, i) => (
                              <TableRow key={String(b.userId || i)}>
                                <TableCell>
                                  <MDTypography variant="button" color="text">
                                    {i + 1}
                                  </MDTypography>
                                </TableCell>
                                <TableCell>
                                  <MDBox display="flex" flexDirection="column">
                                    <MDTypography variant="button" fontWeight="medium">
                                      {b.company || b.name}
                                    </MDTypography>
                                    {b.company ? (
                                      <MDTypography variant="caption" color="text">
                                        {b.name}
                                      </MDTypography>
                                    ) : null}
                                  </MDBox>
                                </TableCell>
                                <TableCell>
                                  <MDTypography variant="caption" color="text">
                                    {b.email || '—'}
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="right">
                                  <MDTypography variant="button">{num(b.bookings)}</MDTypography>
                                </TableCell>
                                <TableCell align="right">
                                  <MDTypography variant="button" fontWeight="medium">
                                    {money(b.spend)}
                                  </MDTypography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </MDBox>
                    )}
                  </PanelCard>
                </MDBox>
              </Grid>
            </Grid>

                <Grid container spacing={3} mt={0.5}>
                  <Grid item xs={12}>
                    <MDBox mt={3} mb={3}>
                      <PanelCard title="Bookings by state" icon="map" color="dark">
                        {(bookings.byState || []).length === 0 ? (
                          <MDTypography variant="button" color="text">
                            {bookings.total
                              ? 'The server did not send a state breakdown — it is probably running an older build.'
                              : 'No bookings in this period.'}
                          </MDTypography>
                        ) : (
                          <MDBox sx={{ overflowX: 'auto' }}>
                            <Table size="small">
                              <TableHead sx={{ display: 'table-header-group' }}>
                                <TableRow>
                                  <TableCell>State</TableCell>
                                  <TableCell align="right">Bookings</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {bookings.byState.map((row) => (
                                  <TableRow key={row.state}>
                                    <TableCell>
                                      <MDTypography variant="button" fontWeight="medium">
                                        {row.state}
                                      </MDTypography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <MDTypography variant="button" fontWeight="medium">
                                        {num(row.count)}
                                      </MDTypography>
                                    </TableCell>
                                  </TableRow>
                                ))}

                                <TableRow>
                                  <TableCell>
                                    <MDTypography variant="button" fontWeight="bold">
                                      All bookings
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <MDTypography variant="button" fontWeight="bold">
                                      {num(bookings.total)}
                                    </MDTypography>
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </MDBox>
                        )}

                        <MDBox mt={1.5}>
                          <MDTypography variant="caption" color="text">
                            Every booking counts here, cancelled and declined included — this is
                            where the demand is, not what was paid.
                          </MDTypography>
                        </MDBox>
                      </PanelCard>
                    </MDBox>
                  </Grid>
                </Grid>

            {/* ---------- Boost + Directory + People ---------- */}
              </>
            ) : null}

            {tab === 1 ? (
              <>
                <SectionHeading
                  title="Hosts & creatives"
                  hint="Hosts list a space, creatives book one."
                />
                <TileRow tiles={tiles.people} />

                <Grid container spacing={3} mt={0.5}>
                  <Grid item xs={12} md={6}>
                    <MDBox mt={3} mb={3}>
                      <PanelCard title="Hosts" icon="villa" color="dark">
                        <StatLine
                          label="Hosts (total)"
                          value={num(data.users.hosts.total)}
                          hint="Non-deleted users with the host role"
                        />
                        <StatLine label="New hosts this period" value={num(data.users.hosts.new)} />
                      </PanelCard>
                    </MDBox>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <MDBox mt={3} mb={3}>
                      <PanelCard title="Creatives" icon="photo_camera" color="primary">
                        <StatLine
                          label="Creatives (total)"
                          value={num(data.users.creatives.total)}
                          hint="Non-deleted users with the creative role"
                        />
                        <StatLine
                          label="New creatives this period"
                          value={num(data.users.creatives.new)}
                        />
                      </PanelCard>
                    </MDBox>
                  </Grid>

                </Grid>

                <Grid container spacing={3} mt={0.5}>
                  <Grid item xs={12}>
                    <MDBox mt={3} mb={3}>
                      <PanelCard title="How new users found us" icon="travel_explore" color="info">
                        {(data.foundUs?.byChannel || []).length === 0 ? (
                          <MDTypography variant="button" color="text">
                            No new signups in this period.
                          </MDTypography>
                        ) : (
                          <MDBox sx={{ overflowX: 'auto' }}>
                            <Table size="small">
                              <TableHead sx={{ display: 'table-header-group' }}>
                                <TableRow>
                                  <TableCell>Source</TableCell>
                                  <TableCell align="right">New users</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {data.foundUs.byChannel.map((r) => (
                                  <TableRow key={r.channel}>
                                    <TableCell>
                                      <MDTypography variant="button" fontWeight="medium">
                                        {r.channel}
                                      </MDTypography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <MDTypography variant="button" fontWeight="medium">
                                        {num(r.count)}
                                      </MDTypography>
                                    </TableCell>
                                  </TableRow>
                                ))}

                                <TableRow>
                                  <TableCell>
                                    <MDTypography variant="button" fontWeight="bold">
                                      All new users
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <MDTypography variant="button" fontWeight="bold">
                                      {num(data.foundUs.total)}
                                    </MDTypography>
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </MDBox>
                        )}

                        <MDBox mt={1.5}>
                          <MDTypography variant="caption" color="text">
                            Self-reported at signup — &ldquo;Other&rdquo; covers anyone who typed a
                            custom answer instead of picking a listed option.
                          </MDTypography>
                        </MDBox>
                      </PanelCard>
                    </MDBox>
                  </Grid>
                </Grid>
              </>
            ) : null}

            {tab === 2 ? (
              <>
                <SectionHeading
                  title="Listings"
                  hint="What hosts put up, and where each listing sits today."
                />
                <TileRow tiles={tiles.listings} />

                <Grid container spacing={3} mt={0.5}>
                  <Grid item xs={12}>
                    <MDBox mt={3} mb={3}>
                      <PanelCard title="Listings by status" icon="home_work" color="secondary">
                        <MDBox sx={{ overflowX: 'auto' }}>
                          <Table size="small">
                            <TableHead sx={{ display: 'table-header-group' }}>
                              <TableRow>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Listings</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {listingBreakdown.map((row) => (
                                <TableRow key={row.label}>
                                  <TableCell>
                                    <MDBox display="flex" alignItems="center" gap={1}>
                                      <MDBox
                                        sx={{
                                          width: 10,
                                          height: 10,
                                          borderRadius: '50%',
                                          backgroundColor: row.dot,
                                        }}
                                      />
                                      <MDTypography variant="button" fontWeight="medium">
                                        {row.label}
                                      </MDTypography>
                                    </MDBox>
                                  </TableCell>
                                  <TableCell align="right">
                                    <MDTypography variant="button" fontWeight="medium">
                                      {num(row.count)}
                                    </MDTypography>
                                  </TableCell>
                                </TableRow>
                              ))}

                              <TableRow>
                                <TableCell>
                                  <MDTypography variant="button" fontWeight="bold">
                                    All listings
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="right">
                                  <MDTypography variant="button" fontWeight="bold">
                                    {num(data.listings.total)}
                                  </MDTypography>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </MDBox>
                      </PanelCard>
                    </MDBox>
                  </Grid>
                </Grid>

                <Grid container spacing={3} mt={0.5}>
                  <Grid item xs={12}>
                    <MDBox mt={3} mb={3}>
                      <PanelCard title="Published listings by state" icon="map" color="info">
                        {(data.listings.byState || []).length === 0 ? (
                          <MDTypography variant="button" color="text">
                            {data.listings.totalPublished
                              ? 'The server did not send a state breakdown — it is probably running an older build.'
                              : 'No published listings to break down.'}
                          </MDTypography>
                        ) : (
                          <MDBox sx={{ overflowX: 'auto' }}>
                            <Table size="small">
                              <TableHead sx={{ display: 'table-header-group' }}>
                                <TableRow>
                                  <TableCell>State</TableCell>
                                  <TableCell align="right">Listings</TableCell>
                                  <TableCell align="right">Share</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {data.listings.byState.map((row) => (
                                  <TableRow key={row.state}>
                                    <TableCell>
                                      <MDTypography variant="button" fontWeight="medium">
                                        {row.state}
                                      </MDTypography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <MDTypography variant="button" fontWeight="medium">
                                        {num(row.count)}
                                      </MDTypography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <MDTypography variant="button" color="text">
                                        {data.listings.totalPublished
                                          ? `${Math.round((row.count / data.listings.totalPublished) * 100)}%`
                                          : '—'}
                                      </MDTypography>
                                    </TableCell>
                                  </TableRow>
                                ))}

                                <TableRow>
                                  <TableCell>
                                    <MDTypography variant="button" fontWeight="bold">
                                      All published
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <MDTypography variant="button" fontWeight="bold">
                                      {num(data.listings.totalPublished)}
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <MDTypography variant="button" fontWeight="bold">
                                      100%
                                    </MDTypography>
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </MDBox>
                        )}
                      </PanelCard>
                    </MDBox>
                  </Grid>
                </Grid>

              </>
            ) : null}

            {tab === 3 ? (
              <>
                <SectionHeading
                  title="Boost"
                  hint="Hosts paying to sit at the top of search."
                />
                <TileRow tiles={tiles.boost} />

                <Grid container spacing={3} mt={0.5}>
                  <Grid item xs={12} md={6}>
                    <MDBox mt={3} mb={3}>
                      <PanelCard title="Boost your listings" icon="rocket_launch" color="success">
                      <StatLine label="Revenue" value={money(boost.revenue)} />
                      <StatLine
                      label="Listing-weeks sold"
                      value={num(boost.weeksSold)}
                      hint="One boost slot = one listing for one week"
                      />
                      <StatLine label="Checkouts" value={num(boost.checkouts)} />
                      <StatLine label="Listings boosted" value={num(boost.listingsBoosted)} />
                      <StatLine label="Hosts boosting" value={num(boost.hostsBoosting)} />
                      <StatLine
                      label="Live right now"
                      value={num(boost.activeNow)}
                      hint="Active boosts whose week contains today — not limited to this period"
                      />
                      </PanelCard>
                    </MDBox>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <MDBox mt={3} mb={3}>
                      <PanelCard title="Where the boosts are" icon="public" color="info">
                        {boost.topStates.length === 0 ? (
                          <MDTypography variant="button" color="text">
                            No boosts sold in this period.
                          </MDTypography>
                        ) : (
                          boost.topStates.map((s) => (
                            <StatLine
                              key={s.state}
                              label={s.state}
                              value={`${num(s.weeks)} wk · ${money(s.revenue)}`}
                            />
                          ))
                        )}
                      </PanelCard>
                    </MDBox>
                  </Grid>
                </Grid>
              </>
            ) : null}

            {tab === 4 ? (
              <>
                <SectionHeading
                  title="Creative directory"
                  hint="Creatives paying to be found by brands."
                />
                <TileRow tiles={tiles.directory} />

                <Grid container spacing={3} mt={0.5}>
                  <Grid item xs={12}>
                    <MDBox mt={3} mb={3}>
                      <PanelCard title="Creative directory" icon="groups" color="warning">
                      <StatLine
                      label="New submissions"
                      value={num(directory.submissions)}
                      hint="Excludes rows still stuck at pending_payment"
                      />
                      <StatLine label="Approved" value={num(directory.approved)} />
                      <StatLine label="Rejected" value={num(directory.rejected)} />
                      <StatLine label="Awaiting moderation" value={num(directory.pending)} />
                      <StatLine label="Approved & live (total)" value={num(directory.liveTotal)} />

                      {directory.byTier.length ? (
                        <>
                          <Divider />
                          {directory.byTier.map((t) => (
                            <StatLine key={t.tier} label={`Tier: ${t.tier}`} value={num(t.count)} />
                          ))}
                        </>
                      ) : null}
                      </PanelCard>
                    </MDBox>
                  </Grid>

                </Grid>
              </>
            ) : null}
          </>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  )
}

export default MonthlyRecap
